import React from 'react';
import { Button } from 'react-bootstrap';
import { useTranslate } from '@tolgee/react';

import type { DeprecatedInterestSuggestion } from './targetingValidation';

type Props = {
  suggestions: DeprecatedInterestSuggestion[];
  onReplace: (suggestion: DeprecatedInterestSuggestion) => void;
  onRemove: (suggestion: DeprecatedInterestSuggestion) => void;
  onReplaceAll?: () => void;
};

export const DeprecatedInterestsNotice: React.FC<Props> = ({
  suggestions,
  onReplace,
  onRemove,
  onReplaceAll
}) => {
  const { t } = useTranslate();
  if (suggestions.length === 0) return null;

  const replaceableCount = suggestions.filter(
    (suggestion) => suggestion.alternative
  ).length;

  return (
    <div
      className="border border-warning rounded bg-warning-subtle p-3"
      role="alert"
      data-testid="deprecated-interests-notice"
    >
      <div className="d-flex align-items-start gap-2 mb-2">
        <i
          className="fa-solid fa-triangle-exclamation text-warning fs-5 mt-1"
          aria-hidden="true"
        />
        <div>
          <div className="fw-semibold fs-6">
            {t(
              'adManager.targeting.deprecatedInterestsTitle',
              'Some interests are no longer available on Meta'
            )}
          </div>
          <div className="text-body-secondary small">
            {t(
              'adManager.targeting.deprecatedInterestsDescription',
              'Replace or remove them before publishing — Meta rejects ads that still target these interests.'
            )}
          </div>
        </div>
      </div>
      <ul className="list-unstyled mb-0 vstack gap-2">
        {suggestions.map((suggestion) => (
          <li
            key={suggestion.deprecated.id}
            className="d-flex flex-column flex-md-row gap-2 align-items-md-center justify-content-between bg-body rounded border p-2"
          >
            <div className="d-flex flex-wrap align-items-center gap-2">
              <span className="fw-semibold text-danger text-decoration-line-through">
                {suggestion.deprecated.name}
              </span>
              {suggestion.alternative ? (
                <>
                  <i
                    className="fa-solid fa-arrow-right text-body-secondary"
                    aria-hidden="true"
                  />
                  <span className="fw-semibold text-success">
                    {suggestion.alternative.name}
                  </span>
                </>
              ) : (
                <span className="text-body-secondary fst-italic">
                  {t(
                    'adManager.targeting.deprecatedInterestNoAlternative',
                    'No alternative available'
                  )}
                </span>
              )}
            </div>
            <div className="d-flex gap-2 flex-shrink-0">
              {suggestion.alternative ? (
                <Button
                  type="button"
                  size="sm"
                  variant="primary"
                  onClick={() => onReplace(suggestion)}
                >
                  {t('adManager.targeting.deprecatedInterestReplace', 'Replace')}
                </Button>
              ) : null}
              <Button
                type="button"
                size="sm"
                variant="outline-secondary"
                onClick={() => onRemove(suggestion)}
              >
                {t('adManager.targeting.deprecatedInterestRemove', 'Remove')}
              </Button>
            </div>
          </li>
        ))}
      </ul>
      {onReplaceAll && replaceableCount > 1 ? (
        <div className="mt-3 d-flex justify-content-end">
          <Button
            type="button"
            size="sm"
            variant="outline-primary"
            onClick={onReplaceAll}
          >
            {t(
              'adManager.targeting.deprecatedInterestReplaceAll',
              'Replace all with alternatives'
            )}
          </Button>
        </div>
      ) : null}
    </div>
  );
};
